// Первым делом — синхронно подсветить стартовый раздел (см. shared/initial-view):
// строго до любых await, чтобы первый кадр уже показывал итоговый раздел.
presetInitialView();

await import('./shared/vendor-globals').catch(() => {});
import { presetInitialView } from './shared/initial-view';
import { state, FREE_MODE_LIMIT } from './shared/state';
import { $, t, applyLang, showToast } from './shared/index';
import { checkAuth, handleLogin, handleRegister, switchView, checkServerHealth, showLogin, showRegistrationPrompt, stopHealthPolling, exportBalanceHistoryCsv, enterFreeMode, disableGuestMode, prepareRegistrationForm, isGuestMode, switchAuthTab } from './auth';
import { autoResizeTextarea, updateWelcomeHints, setWelcomeVisible, resizeCategorySelect } from './shared/ui';
import { newConversation, handleSend, stopGeneration, handleFiles, initMessagesDelegation } from './conversation/conversation';
import { initDeferredOrder, stopDeferredWatch } from './conversation/deferred';
import { initAdminTabs } from './admin/admin';
import { SessionManager } from './session/sessions';
import { initUserDocuments } from './kb/user-documents';
import { initConversationSearch } from './conversation/conversation-search';
import { initPaymentModal, openCreditsModal, closeCreditsModal } from './billing/payment-modal';
import { initLargeRequestModal } from './billing/large-request';
import { applyCategorySystemPrompt } from './shared/system-prompt';
import { sessionStore } from './session/session-store';
import { stopAllAttachmentPolling } from './session/session-attachments';
import {
  closeBooksModal,
  consumeDocRouteFromLocation,
  initDocRouteHandling,
  openBooksModal as openBooksModalNav,
  syncDocRouteToLocation,
  DOC_ROUTE_PREFIX,
} from './kb/books-nav';
import { initJourney } from './journey/journey';
import { initVoiceInput } from './onboarding/voice';
// Mounts the onboarding overlay: its module registers the shared
// onboarding-gateway implementation used by the journey view (ADR-002 C).
import './onboarding/onboarding';
import { bindAdminExamplesEvents } from './admin/admin-examples';
import { initAvgVideo } from './avg-video/avg-video';
import { initAvgDialog } from './avg-dialog/avg-dialog';
import { initCouncil, stopCouncilWatch } from './council/council';
import { bindAdminAvgVideoEvents } from './admin/admin-avg-video';
import { bindAdminAvgVideoCollectionsEvents } from './admin/admin-avg-video-collections';
import { bindAdminAvgDialogEvents } from './admin/admin-avg-dialog';
import { bindAdminAvgBroadcastEvents } from './admin/admin-avg-broadcast';
import { initTelegramLink, stopTelegramLinkPolling } from './telegram-link';
import { initApiKeys } from './api-keys';
import type { AppUser } from './shared/types';

function init() {
  localStorage.removeItem('gemma_n_predict');
  applyTheme();
  applyLang();
  bindEvents();
  initMessagesDelegation();
  initDeferredOrder();
  initLargeRequestModal();
  initPaymentModal();
  autoResizeTextarea();
  checkAuth();
  initAdminTabs();
  initUserDocuments();
  initConversationSearch();
  initJourney();
  initVoiceInput();
  bindAdminExamplesEvents();
  bindAdminAvgVideoEvents();
  bindAdminAvgVideoCollectionsEvents();
  bindAdminAvgDialogEvents();
  bindAdminAvgBroadcastEvents();
  initAvgVideo();
  initAvgDialog();
  initCouncil();
  initTelegramLink();
  initApiKeys();
  initDocRouteHandling();
  void consumeDocRouteFromLocation();

  if (typeof marked !== 'undefined') {
    marked.setOptions({
      breaks: true,
      gfm: true,
      highlight: (code: string, lang?: string) => {
        if (lang && window.hljs && hljs.getLanguage(lang)) return hljs.highlight(code, { language: lang }).value;
        if (window.hljs) return hljs.highlightAuto(code).value;
        return code;
      }
    } as Parameters<typeof marked.setOptions>[0]);
  }
}

async function saveSettings(): Promise<boolean> {
  if (isGuestMode()) {
    const activityCount = parseInt(localStorage.getItem('avgexpert_free_activity_count') || '0', 10);
    if (activityCount >= FREE_MODE_LIMIT) {
      showToast(`Вы исчерпали лимит бесплатных запросов (максимум ${FREE_MODE_LIMIT}). Пожалуйста, зарегистрируйтесь.`, 'error');
      showRegistrationPrompt();
      return false;
    }
    localStorage.setItem('avgexpert_free_activity_count', String(activityCount + 1));
  }

  localStorage.setItem('gemma_lang', state.lang);
  const payload: Record<string, unknown> = {};
  const user = state.currentUser;

  const usernameEl = $<HTMLInputElement>('user-username');
  if (usernameEl) {
    const trimmed = usernameEl.value.trim();
    if (trimmed && trimmed !== (user?.username || '')) {
      payload.username = trimmed;
    }
  }

  const emailEl = $<HTMLInputElement>('user-email');
  if (emailEl && emailEl.value !== (user?.email || '')) {
    payload.email = emailEl.value;
  }

  const passwordEl = $<HTMLInputElement>('user-password');
  if (passwordEl?.value) {
    payload.password = passwordEl.value;
  }

  const defaultCatSel = $<HTMLSelectElement>('user-default-category');
  if (defaultCatSel?.value && defaultCatSel.value !== (user?.category || '')) {
    payload.category = defaultCatSel.value;
  }

  const canEditSystemPrompt = !!(user?.is_admin || user?.show_prompt_to_user);
  const promptEl = $<HTMLTextAreaElement>('user-system-prompt');
  if (promptEl && canEditSystemPrompt) {
    const promptVal = promptEl.value || null;
    const currentPrompt = user?.system_prompt || null;
    if (promptVal !== currentPrompt) {
      payload.system_prompt = promptVal;
    }
  }

  // Видеоответы Диалога: выключенные означают «голос на заставке» — ответ
  // приходит сразу, без ожидания рендера говорящей головы.
  const videoEl = $<HTMLInputElement>('user-dialog-video');
  if (videoEl && videoEl.checked !== (user?.dialog_video_enabled !== false)) {
    payload.dialog_video_enabled = videoEl.checked;
  }

  const { readUiModulesFromForm, getUserUiModules } = await import('./shared/ui-modules');
  const nextModules = readUiModulesFromForm();
  const currentModules = getUserUiModules(user);
  if (
    nextModules.journey !== currentModules.journey
    || nextModules.examples !== currentModules.examples
  ) {
    payload.ui_modules = nextModules;
  }

  // Профиль аватара идёт своим маршрутом, а не в payload PATCH /api/users/me:
  // проверка доступности профиля живёт в модуле Диалога (L3), а auth — L1.
  const avatarSel = $<HTMLSelectElement>('user-avatar-profile');
  const avatarContainer = $('user-avatar-profile-container');
  if (avatarSel && !avatarContainer?.classList.contains('hidden')) {
    const nextAvatarId = avatarSel.value || '';
    if (nextAvatarId !== ((user?.dialog_avatar_id as string | null) || '')) {
      const { saveAvatarProfile } = await import('./shared/avatar-profile');
      const result = await saveAvatarProfile(nextAvatarId);
      if (!result.ok) {
        showToast(result.error, { variant: 'error' });
        // Платный аватар у Free-аккаунта: зовём регистрацию, а не просто ругаемся.
        if (result.code === 'registration_required') showRegistrationPrompt();
        return false;
      }
      if (state.currentUser) state.currentUser.dialog_avatar_id = nextAvatarId || null;
    }
  }

  if (Object.keys(payload).length > 0) {
    try {
      const r = await fetch('/api/users/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + state.authToken },
        body: JSON.stringify(payload)
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({})) as { detail?: string; error?: string; errors?: unknown };
        const zodHint = Array.isArray(data.errors) && data.errors.length > 0
          ? ' Проверьте формат полей профиля.'
          : '';
        showToast((data.error || data.detail || 'Не удалось сохранить настройки') + zodHint, { variant: 'error' });
        return false;
      }

      const data = await r.json().catch(() => ({})) as { token?: string };
      if (data.token) {
        state.authToken = data.token;
        localStorage.setItem('avgexpert_token', data.token);
      }

      if (state.currentUser) {
        if (payload.username) {
          state.currentUser.username = payload.username as string;
        }
        if (payload.password) {
          const pwd = $<HTMLInputElement>('user-password');
          if (pwd) pwd.value = '';
        }
        if (payload.category) {
          state.currentUser.category = payload.category as string;

          const conversationSessionCat = $<HTMLSelectElement>('conversation-session-category');
          if (conversationSessionCat) {
            conversationSessionCat.value = payload.category as string;
          }

          const titleEl = $('conversation-title-category');
          if (titleEl) {
            titleEl.textContent = payload.category as string;
          }

          applyCategorySystemPrompt(payload.category as string);
        }
        if (payload.system_prompt !== undefined) {
          state.currentUser.system_prompt = payload.system_prompt as string;
          const conversationSessionCat = $<HTMLSelectElement>('conversation-session-category');
          applyCategorySystemPrompt(conversationSessionCat?.value || state.currentUser.category);
        }
        if (payload.ui_modules) {
          state.currentUser.ui_modules = payload.ui_modules as AppUser['ui_modules'];
          const { applyUiModulesNav } = await import('./shared/ui-modules');
          applyUiModulesNav(state.currentUser);
        }
      }
    } catch (err) {
      console.error(err);
      showToast('Ошибка сети при сохранении настроек', { variant: 'error' });
      return false;
    }
  }
  showToast(t('saved'));
  return true;
}

function applyTheme(theme: string = localStorage.getItem('avgexpert_theme') || 'system') {
  const normalizedTheme = ['light-business', 'light-contrast'].includes(theme)
    ? 'light'
    : (['light', 'dark', 'system'].includes(theme) ? theme : 'system');
  if (normalizedTheme === 'system') {
    document.documentElement.removeAttribute('data-theme');
  } else {
    document.documentElement.setAttribute('data-theme', normalizedTheme);
  }
  localStorage.setItem('avgexpert_theme', normalizedTheme);
  document.querySelectorAll('[data-theme-option]').forEach(btn => {
    const el = btn as HTMLElement;
    const isActive = el.dataset.themeOption === normalizedTheme;
    el.classList.toggle('active', isActive);
    el.setAttribute('aria-checked', String(isActive));
  });
}

function logout() {
  localStorage.removeItem('avgexpert_token');
  disableGuestMode();
  sessionStorage.removeItem('avgexpert_guest_sessions');
  for (let i = sessionStorage.length - 1; i >= 0; i--) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith('avgexpert_guest_sessions_data_')) {
      sessionStorage.removeItem(key);
    }
  }
  state.authToken = null;
  state.currentUser = null;
  state.categories = {};
  stopHealthPolling();
  stopTelegramLinkPolling();
  stopDeferredWatch();
  // Сторож решений куратора опрашивает сервер от имени вышедшего человека —
  // гасим вместе с сессией (C-2).
  stopCouncilWatch();
  $('telegram-link-card')?.classList.add('hidden');
  sessionStore.clear();
  stopAllAttachmentPolling();
  if ($('messages')) $('messages')!.textContent = '';
  if ($('attached-docs')) $('attached-docs')!.textContent = '';
  const conversationSessionCat = $<HTMLSelectElement>('conversation-session-category');
  if (conversationSessionCat) conversationSessionCat.innerHTML = '';
  setWelcomeVisible(true);
  showLogin();
}

function closeAppMenu() {
  $('app-menu')?.classList.remove('open');
  $('app-menu-btn')?.setAttribute('aria-expanded', 'false');
}

function closeAdvancedPanel() {
  $('advanced-panel')?.classList.add('hidden');
  $('advanced-toggle-btn')?.classList.remove('active');
  $('input-wrapper')?.classList.remove('advanced-open');
}

function openAboutModal() {
  $('about-modal')?.classList.remove('hidden');
  closeAppMenu();
  $<HTMLButtonElement>('about-modal-close')?.focus();
}

function closeAboutModal() {
  $('about-modal')?.classList.add('hidden');
}

async function openHelpModal() {
  const modal = $('help-modal');
  modal?.classList.remove('hidden');
  closeAppMenu();
  $<HTMLButtonElement>('help-modal-close')?.focus();
  await loadHelpDocument('Help.md');
}

async function loadHelpDocument(fileName: string) {
  const content = $('help-content');
  if (!content) return;

  const safeFile = String(fileName || 'Help.md').replace(/[^a-zA-Z0-9_.-]/g, '');
  if (content.dataset.loadedFile === safeFile) return;

  document.querySelectorAll('[data-help-file]').forEach(btn => {
    const el = btn as HTMLElement;
    el.classList.toggle('active', el.dataset.helpFile === safeFile);
  });

  content.innerHTML = '<div class="help-loading"><span class="help-loader"></span><span>Загрузка документа...</span></div>';

  try {
    const response = await fetch(`assets/${safeFile}`, { cache: 'no-cache' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const markdown = await response.text();
    content.innerHTML = DOMPurify.sanitize(marked.parse(markdown) as string);
    content.dataset.loadedFile = safeFile;
    content.scrollTop = 0;
    content.querySelectorAll('a').forEach(link => {
      if (link.hostname && link.hostname !== window.location.hostname) {
        link.target = '_blank';
        link.rel = 'noopener';
      }
    });
  } catch {
    content.innerHTML = `<div class="help-error"><strong>Не удалось загрузить документ.</strong><span>Проверьте файл assets/${DOMPurify.sanitize(safeFile)} и доступность сервера.</span></div>`;
  }
}

function closeHelpModal() {
  $('help-modal')?.classList.add('hidden');
}

async function openBooksModal() {
  closeAppMenu();
  await openBooksModalNav();
  syncDocRouteToLocation(DOC_ROUTE_PREFIX, true);
}

function bindEvents() {
  document.querySelectorAll('[data-view]').forEach(btn => btn.addEventListener('click', () => {
    closeAppMenu();
    switchView((btn as HTMLElement).dataset.view || 'conversation');
  }));
  $('new-conversation-btn')?.addEventListener('click', () => newConversation());
  $('save-btn')?.addEventListener('click', async () => {
    const success = await saveSettings();
    if (success) {
      switchView('conversation');
    }
  });
  const loginForm = $('login-form');
  if (loginForm) {
    loginForm.addEventListener('submit', handleLogin);
  } else {
    console.error('Login form not found during bindEvents');
  }
  $('login-submit-btn')?.addEventListener('click', (e: MouseEvent) => {
    if (!$('login-form')) handleLogin(e);
  });

  const registerForm = $('register-form');
  if (registerForm) {
    registerForm.addEventListener('submit', handleRegister);
  }

  $('register-submit-btn')?.addEventListener('click', (e: MouseEvent) => {
    if (!$('register-form')) handleRegister(e);
  });

  $('show-register-link')?.addEventListener('click', (e: MouseEvent) => {
    e.preventDefault();
    void prepareRegistrationForm('register');
  });

  $('tab-login')?.addEventListener('click', () => switchAuthTab('login'));
  $('tab-register')?.addEventListener('click', () => {
    void prepareRegistrationForm('register');
  });

  $('auth-logo-switch')?.addEventListener('click', () => {
    const isLogin = !$('login-form')?.classList.contains('hidden');
    if (isLogin) {
      void prepareRegistrationForm('register');
    } else {
      switchAuthTab('login');
    }
  });

  document.querySelectorAll<HTMLButtonElement>('.auth-pw-toggle').forEach((btn) => {
    btn.addEventListener('click', () => {
      const input = btn.parentElement?.querySelector<HTMLInputElement>('input');
      const icon = btn.querySelector<HTMLElement>('.auth-pw-toggle-icon');
      if (!input || !icon) return;
      const visible = input.type === 'text';
      input.type = visible ? 'password' : 'text';
      icon.textContent = visible ? '👁️' : '🙈';
      btn.setAttribute('aria-label', visible ? 'Показать пароль' : 'Скрыть пароль');
    });
  });

  $('enter-free-mode-link')?.addEventListener('click', (e: MouseEvent) => {
    e.preventDefault();
    void enterFreeMode();
  });

  // Обёртка Mini App (telegram-app.js) живёт до бандла и про auth.ts не знает —
  // форму входа она просит событием, как Диалог сообщает о ходе событием
  // avg-dialog:turn-committed. Гостевой режим форму прячет, событие — штатный
  // способ показать её по явному желанию человека.
  document.addEventListener('avgexpert:show-login', () => showLogin());

  $('show-login-link')?.addEventListener('click', (e: MouseEvent) => {
    e.preventDefault();
    switchAuthTab('login');
  });

  $('credits-menu-btn')?.addEventListener('click', () => {
    closeAppMenu();
    openCreditsModal();
  });
  $('settings-topup-btn')?.addEventListener('click', () => openCreditsModal());
  $('balance-history-export-btn')?.addEventListener('click', exportBalanceHistoryCsv);
  $('about-menu-btn')?.addEventListener('click', openAboutModal);
  $('about-modal-close')?.addEventListener('click', closeAboutModal);
  $('about-modal-backdrop')?.addEventListener('click', closeAboutModal);
  $('help-menu-btn')?.addEventListener('click', openHelpModal);
  $('help-modal-close')?.addEventListener('click', closeHelpModal);
  $('help-modal-backdrop')?.addEventListener('click', closeHelpModal);
  $('help-modal')?.addEventListener('click', (event: MouseEvent) => {
    const target = event.target as Element;
    const btn = target.closest?.('[data-help-file]') as HTMLElement | null;
    if (btn) loadHelpDocument(btn.dataset.helpFile || 'Help.md');
  });
  document.querySelectorAll('[data-help-file]').forEach(btn => {
    btn.addEventListener('click', () => loadHelpDocument((btn as HTMLElement).dataset.helpFile || 'Help.md'));
  });
  $('docs-menu-btn')?.addEventListener('click', openBooksModal);
  $('books-modal-close')?.addEventListener('click', closeBooksModal);
  $('books-modal-backdrop')?.addEventListener('click', closeBooksModal);

  $('send-btn')?.addEventListener('click', handleSend);
  $('user-input')?.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      // On touch-primary devices (phones/tablets) the virtual keyboard's
      // "Enter" inserts a newline — only the send-btn should submit.
      const isTouchPrimary = window.matchMedia('(pointer: coarse)').matches;
      if (!isTouchPrimary) {
        e.preventDefault();
        handleSend();
      }
    }
  });
  $('stop-btn')?.addEventListener('click', stopGeneration);

  const fileInput = $<HTMLInputElement>('file-input');
  $('attach-btn')?.addEventListener('click', () => fileInput?.click());
  fileInput?.addEventListener('change', (e: Event) => {
    const input = e.target as HTMLInputElement;
    handleFiles(input.files);
  });

  const wrapper = $('input-wrapper');
  const dropOverlay = $('drop-overlay');
  if (wrapper && dropOverlay) {
    ['dragenter', 'dragover'].forEach(ev => wrapper.addEventListener(ev, (e: Event) => { e.preventDefault(); dropOverlay.classList.add('active'); }));
    ['dragleave', 'drop'].forEach(ev => dropOverlay.addEventListener(ev, (e: Event) => { e.preventDefault(); dropOverlay.classList.remove('active'); }));
    dropOverlay.addEventListener('drop', (e: DragEvent) => {
      if (e.dataTransfer?.files) handleFiles(e.dataTransfer.files);
    });
  }

  $('sidebar-toggle')?.addEventListener('click', () => {
    const sidebar = $('sidebar');
    if (!sidebar) return;
    const isDesktop = window.innerWidth >= 1024;
    if (isDesktop) {
      sidebar.classList.toggle('collapsed');
    } else {
      sidebar.classList.toggle('open');
      $('sidebar-backdrop')?.classList.toggle('active', sidebar.classList.contains('open'));
    }
  });

  $('logo-icon-toggle')?.addEventListener('click', () => {
    const sidebar = $('sidebar');
    if (!sidebar) return;
    if (!sidebar.classList.contains('collapsed')) return;
    sidebar.classList.remove('collapsed');
  });

  function openSidebar() {
    $('sidebar')?.classList.add('open');
    $('sidebar-backdrop')?.classList.add('active');
  }
  function closeSidebar() {
    $('sidebar')?.classList.remove('open');
    $('sidebar-backdrop')?.classList.remove('active');
  }
  // Бургер есть в шапке каждого вью. Перечисление по id уже приводило к тому,
  // что новые вью (Примеры, Видео, Диалог) оставались без обработчика и с
  // мобильного из них было не выйти. Ловим весь класс разом — новый вью
  // получает рабочее меню без правок здесь.
  document.querySelectorAll('.mobile-menu-btn').forEach(btn => btn.addEventListener('click', openSidebar));
  $('conversation-header-category-trigger')?.addEventListener('click', openSidebar);
  $('sidebar-backdrop')?.addEventListener('click', closeSidebar);

  // Swipe-to-close sidebar on touch devices
  (() => {
    const sidebar = $('sidebar');
    if (!sidebar) return;
    let touchStartX = 0;
    let touchStartY = 0;
    let tracking = false;

    sidebar.addEventListener('touchstart', (e: TouchEvent) => {
      const t = e.touches[0];
      touchStartX = t.clientX;
      touchStartY = t.clientY;
      tracking = true;
    }, { passive: true });

    sidebar.addEventListener('touchmove', (e: TouchEvent) => {
      if (!tracking) return;
      const t = e.touches[0];
      const dx = t.clientX - touchStartX;
      const dy = Math.abs(t.clientY - touchStartY);
      // Only track horizontal swipe left (close direction)
      if (dx < -50 && dy < 40) {
        tracking = false;
        closeSidebar();
      }
    }, { passive: true });

    sidebar.addEventListener('touchend', () => { tracking = false; }, { passive: true });
  })();

  $('app-menu-btn')?.addEventListener('click', () => {
    const menu = $('app-menu');
    const isOpen = menu?.classList.toggle('open') || false;
    $('app-menu-btn')?.setAttribute('aria-expanded', String(isOpen));
  });

  document.querySelectorAll('[data-theme-option]').forEach(btn => {
    btn.addEventListener('click', () => {
      applyTheme((btn as HTMLElement).dataset.themeOption || 'system');
      closeAppMenu();
    });
  });

  $('logout-btn')?.addEventListener('click', logout);

  const aboutPhoto = document.getElementById('about-photo-img');
  if (aboutPhoto) {
    aboutPhoto.addEventListener('error', () => {
      aboutPhoto.parentElement?.classList.add('about-photo-missing');
      aboutPhoto.remove();
    });
  }

  $('user-input')?.addEventListener('input', autoResizeTextarea);

  $('advanced-toggle-btn')?.addEventListener('click', () => {
    const panel = $('advanced-panel');
    const isOpen = panel?.classList.toggle('hidden') === false;
    $('advanced-toggle-btn')?.classList.toggle('active', isOpen);
    $('input-wrapper')?.classList.toggle('advanced-open', isOpen);
  });

  document.addEventListener('click', (e: MouseEvent) => {
    const target = e.target as Node;
    const appMenu = $('app-menu');
    if (appMenu && !appMenu.contains(target)) closeAppMenu();

    const panel = $('advanced-panel');
    const btn = $('advanced-toggle-btn');
    if (panel && btn && !panel.contains(target) && !btn.contains(target)) {
      closeAdvancedPanel();
    }
  });

  document.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeAppMenu();
      closeAdvancedPanel();
      closeCreditsModal();
      closeAboutModal();
      closeHelpModal();
      closeBooksModal();
    }
  });

  $('conversation-session-category')?.addEventListener('change', async () => {
    const isGuestMode = () => localStorage.getItem('avgexpert_guest_mode') === '1';
    const selectedCat = $<HTMLSelectElement>('conversation-session-category')?.value;
    if (isGuestMode() && selectedCat && selectedCat !== 'Консультант') {
      $<HTMLSelectElement>('conversation-session-category')!.value = 'Консультант';
      const titleEl = $('conversation-title-category');
      if (titleEl) {
        titleEl.textContent = 'Консультант';
      }
      resizeCategorySelect();
      applyCategorySystemPrompt('Консультант');

      const auth = await import('./auth');
      auth.showRegistrationPrompt();
      return;
    }

    const titleEl = $('conversation-title-category');
    if (titleEl && selectedCat) {
      titleEl.textContent = selectedCat;
    }
    resizeCategorySelect();
    applyCategorySystemPrompt(selectedCat);
    SessionManager.saveCurrent();
    if (state.authToken && state.currentUser) checkServerHealth();
    updateWelcomeHints();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
